<template>
  <div>
    <loading :loading="loading"></loading>

    <error-alert-item
      :error-alert-event="errorAlertEvent"
      :network-error="networkError"
      @resetErrorAlertEvent="errorAlertEvent=false">
    </error-alert-item>

    <single-message-dialog
      :show="showSingleMessageDialog"
      :message="fileValidationMessage"
      button-text="Dismiss"
      @onContinue="showSingleMessageDialog=false"
      ></single-message-dialog>

    <file-password-details-dialog
      ref="filePasswordDetailsDialog"
      :allow-dismiss-without-password="allowDismissWithoutPassword"
      :show="showFilePasswordDialog"
      @onDialogClose="onDocumentCreatedOrUpdatedButPasswordNotSaved"
      @onPasswordEntered="onFilePasswordEntered"
      ></file-password-details-dialog>
  </div>
</template>

<script>
  import ErrorAlertItem from "./ErrorAlertItem";
  import EzUtils from "../../utils/EzUtils";
  import DataSource from "../../data/DataSource";
  import Loading from "./Loading";
  import AlertService from "../core/alert/AlertService";
  import FilePasswordDetailsDialog from "../dialogs/FilePasswordDetailsDialog";
  import { DocumentStatus } from "../../utils/DocumentDetails";
  import SingleMessageDialog from "../dialogs/SingleMessageDialog";

  export default {
    name: "FileUploadItem",
    components: { ErrorAlertItem, Loading, FilePasswordDetailsDialog, SingleMessageDialog },
    data: () => ({
      loading: false,
      networkError: {},
      errorAlertEvent: false,
      showFilePasswordDialog: false,
      genericResponse: {},
      entityType: null,
      entityId: -1,
      documentName: "",
      documentDescription: "",
      documentId: -1,
      documentComment: "",
      additionalData: {},
      fileValidationMessage: "",
      showSingleMessageDialog: false,
    }),

    props: {
      allowOnlyPDF: {
        type: Boolean,
        default: false
      },

      showMessageOnUpload: {
        type: Boolean,
        default: true
      },

      messageToShowOnUpload: {
        type: String,
        default: "Document uploaded successfully."
      },

      updateDocumentStatus: {
        type: String,
        default: "NOT_VERIFIED"
      },

      allowDismissWithoutPassword: {
        type: Boolean,
        default: false
      }
    },

    methods: {
      checkForFileValidation(file) {
        if (!file) {
          this.fileValidationMessage = "Please attach file.";
          this.showSingleMessageDialog = true;
          return false;
        }

        if (this.allowOnlyPDF && !EzUtils.isFilePDF(file)) {
          this.fileValidationMessage = "File format not supported, Please upload PDF file.";
          this.showSingleMessageDialog = true;
          return false;
        }

        if (!EzUtils.isFileAllowedToUpload(file)) {
          this.fileValidationMessage = "File format not supported, Please upload image or PDF file.";
          return false;
        }

        return true;
      },

      showError(error) {
        this.loading = false;
        this.networkError = error;
        this.errorAlertEvent = true;
      },

      showToast(message) {
        AlertService.showToast(message);
      },

      deleteDocument(entityType, entityId, documentId) {
        this.loading = true;
        DataSource.removeDocument(entityType, entityId, documentId)
          .then(res => {
            this.loading = false;
            if (res.errorOccurred) {
              this.showError(res.data)
            } else {
              this.genericResponse = res.data;
              this.onDocumentDeleted();
            }
          });
      },

      createDocument(
        entityType,
        entityId,
        documentName,
        documentDescription,
        subDirectory,
        file,
        additionalData
      ) {
        if (!this.checkForFileValidation(file)) {
          return;
        }

        this.entityType = entityType;
        this.entityId = entityId;
        this.documentName = documentName;
        this.documentDescription = documentDescription;
        this.documentId = -1;
        this.documentComment = "";
        this.additionalData = additionalData;

        const pdfFile = EzUtils.isFilePDF(file);
        this.loading = true;
        DataSource.createDocument(
          entityType,
          entityId,
          documentName,
          documentDescription,
          subDirectory,
          file
        )
          .then(createDocumentRes => {
            this.loading = false;
            if (createDocumentRes.errorOccurred) {
              this.showError(createDocumentRes.data);
            } else {
              this.genericResponse = createDocumentRes.data;
              this.documentId = this.genericResponse.resourceId;
              if (pdfFile) {
                this.checkIfFilePasswordProtectedAndChangeStatus();
              } else {
                this.onDocumentUploaded();
              }
            }
          })
      },

      updateDocument(
        entityType,
        entityId,
        documentId,
        documentName,
        documentDescription,
        file,
        documentComment,
        additionalData
      ) {
        if (file && !this.checkForFileValidation(file)) {
          return;
        }
        this.entityType = entityType;
        this.entityId = entityId;
        this.documentName = documentName;
        this.documentDescription = documentDescription;
        this.documentId = documentId;
        this.documentComment = documentComment;
        this.additionalData = additionalData;

        const pdfFile = file && EzUtils.isFilePDF(file);

        this.loading = true;
        DataSource.updateDocument(
          entityType,
          entityId,
          documentId,
          documentComment,
          documentName,
          documentDescription,
          this.updateDocumentStatus,
          file
        )
          .then(updateDocumentRes => {
            this.loading = false;
            if (updateDocumentRes.errorOccurred) {
              this.showError(updateDocumentRes.data);
            } else {
              this.genericResponse = updateDocumentRes.data;
              this.documentId = this.genericResponse.resourceId;
              if (pdfFile) {
                this.checkIfFilePasswordProtectedAndChangeStatus();
              } else {
                this.onDocumentUploaded();
              }
            }
          })
      },

      askAndUpdatePassword(entityType, entityId, documentId, documentDescription, fileName) {
        this.entityType = entityType;
        this.entityId = entityId;
        this.documentId = documentId;
        this.documentDescription = documentDescription;

        this.openPasswordDialog(fileName, "Enter file password");
      },

      onDocumentCreatedOrUpdated(genericResponse, pdfFile) {
        this.genericResponse = genericResponse;
        this.documentId = genericResponse.resourceId;
        if (pdfFile) {
          this.checkIfFilePasswordProtectedAndChangeStatus();
        } else {
          this.onDocumentUploaded();
        }
      },

      checkIfFilePasswordProtectedAndChangeStatus() {
        this.loading = true;
        DataSource.checkPdfPassword({
          entityType: this.entityType,
          entityId: this.entityId,
          documentId: this.documentId
        })
          .then(passwordRes => {
            this.loading = false;
            if (passwordRes.errorOccurred) {
              this.showError(passwordRes.data);
              this.onDocumentCreatedOrUpdatedButPasswordNotSaved();
            } else {
              const passwordData = passwordRes.data;
              if (passwordData.passwordValid) {
                this.onDocumentUploaded();
              } else {
                this.loading = true;
                DataSource.updateDocument(
                  this.entityType,
                  this.entityId,
                  this.documentId,
                  null,
                  null,
                  null,
                  DocumentStatus.PASSWORD_PENDING
                )
                  .then(updateDocumentRes => {
                    this.loading = false;
                    if (updateDocumentRes.error) {
                      this.showError(updateDocumentRes.data);
                      this.onDocumentCreatedOrUpdatedButPasswordNotSaved();
                    } else {
                      this.openPasswordDialog(passwordData.fileName, passwordData.message);
                    }
                  })
              }
            }
          })
      },

      openPasswordDialog(fileName, message) {
        this.showFilePasswordDialog = true;
        this.$refs.filePasswordDetailsDialog.initFilePasswordDialog(fileName, message);
      },

      onFilePasswordEntered(password) {
        this.showFilePasswordDialog = false;
        this.loading = true;
        DataSource.checkPdfPassword({
          entityType: this.entityType,
          entityId: this.entityId,
          documentId: this.documentId,
          password: password
        })
          .then(passwordRes => {
            this.loading = false;
            if (passwordRes.errorOccurred) {
              this.showError(passwordRes.data);
              this.onDocumentCreatedOrUpdatedButPasswordNotSaved();
            } else {
              const passwordData = passwordRes.data;
              if (passwordData.passwordValid) {
                DataSource.updateDocument(
                  this.entityType,
                  this.entityId,
                  this.documentId,
                  null,
                  null,
                  `${this.documentDescription} ( ${password} ) `,
                  this.updateDocumentStatus
                )
                  .then(updateDocumentRes => {
                    this.loading = false;
                    if (updateDocumentRes.errorOccurred) {
                      this.showError(updateDocumentRes.data);
                      this.onDocumentCreatedOrUpdatedButPasswordNotSaved();
                    } else {
                      this.genericResponse = updateDocumentRes.data;
                      this.onDocumentUploaded();
                    }
                  })
              } else {
                this.openPasswordDialog(passwordData.fileName, passwordData.message);
              }
            }
          })
      },

      onDocumentUploaded() {
        this.loading = false;
        if (this.showMessageOnUpload) {
          this.showToast(this.messageToShowOnUpload);
        }
        this.$emit("onDocumentUploaded", {
          documentUploadResponse: this.genericResponse,
          additionalData: this.additionalData
        });
      },

      onDocumentCreatedOrUpdatedButPasswordNotSaved() {
        this.loading = false;
        this.showFilePasswordDialog = false;
        this.$emit("onDocumentCreatedOrUpdatedButPasswordNotSaved", {
          documentUploadResponse: this.genericResponse,
          additionalData: this.additionalData
        });
      },

      onDocumentDeleted() {
        this.loading = false;
        this.$emit("onDocumentDeleted", {
          documentUploadResponse: this.genericResponse,
          additionalData: this.additionalData
        });
      },

    }
  }

</script>

<style scoped></style>
